import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarkersAPI } from '../../../api/MarkerEndpoints';
import { ArtistsAPI } from '../../../api/ArtistsAPI';
import { ALL_GENRES, ALL_DRESSCODES, ALL_SPECIAL_TYPES, ALL_PRICES } from '../../../constants/EventConstants';
import './Marker-UI.css';
import './EventCreation-UI.css';
import PageTitle from '../../../components/PageTitle';

const BOX_OFFICE_AVAILABILITY = [
  { value: 'UNLIMITED',    label: 'Unlimited' },
  { value: 'LIMITED',      label: 'Limited' },
  { value: 'PRESALE_ONLY', label: 'Presale Only' },
];

const IcoSection = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <path d={d} />
  </svg>
);

export default function EventCreation() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    descriptionGerman: '',
    descriptionEnglish: '',
    selectedGenres: [],
    instagramImageUrl: '',
    instagramName: '',
    address: '',
    entryPrice: '',
    minAge: '',
    dressCode: '',
    drinkPriceIndicator: '',
    latitude: '',
    longitude: '',
    markerType: 'Event',
    specialType: '',
    ticketUrl: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    hostCode: '',
    boxOfficePrice: '',
    boxOfficeAvailability: '',
    hasAwarenessTeam: false,
  });

  const [artists, setArtists] = useState([{ name: '', instagram: '', artistSpotifyId: '' }]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // autocomplete
  const [allArtists, setAllArtists] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(-1);   // row index with open suggestions
  const [spotifyStatus, setSpotifyStatus] = useState({}); // { [rowIndex]: 'loading'|'found'|'none' }
  const spotifyTimers = useRef({});

  useEffect(() => {
    ArtistsAPI.getAll()
      .then(d => setAllArtists(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const getSuggestions = (name) => {
    if (!name || name.length < 2) return [];
    const q = name.toLowerCase();
    return allArtists.filter(a => a.name?.toLowerCase().includes(q)).slice(0, 6);
  };

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const setArtistField = (index, field, value) =>
    setArtists(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));

  const addArtist = () => {
    setArtists(prev => [...prev, { name: '', instagram: '', artistSpotifyId: '' }]);
    setOpenDropdown(-1);
  };

  const removeArtist = (i) => {
    setArtists(prev => prev.filter((_, idx) => idx !== i));
    setOpenDropdown(-1);
    clearTimeout(spotifyTimers.current[i]);
  };

  const handleArtistNameChange = (index, value) => {
    setArtistField(index, 'name', value);
    setOpenDropdown(value.length >= 2 ? index : -1);

    // debounced Spotify lookup for new artists
    clearTimeout(spotifyTimers.current[index]);
    if (value.trim().length >= 2) {
      setSpotifyStatus(s => ({ ...s, [index]: 'loading' }));
      spotifyTimers.current[index] = setTimeout(async () => {
        try {
          const res = await ArtistsAPI.searchSpotify(value.trim());
          if (res?.spotifyId) {
            setArtistField(index, 'artistSpotifyId', res.spotifyId);
            setSpotifyStatus(s => ({ ...s, [index]: 'found' }));
          } else {
            setSpotifyStatus(s => ({ ...s, [index]: 'none' }));
          }
        } catch {
          setSpotifyStatus(s => ({ ...s, [index]: 'none' }));
        }
      }, 600);
    } else {
      setSpotifyStatus(s => ({ ...s, [index]: undefined }));
    }
  };

  const selectSuggestion = (index, artist) => {
    clearTimeout(spotifyTimers.current[index]);
    setArtists(prev => prev.map((a, i) => i === index ? {
      name: artist.name || '',
      instagram: artist.instagram || '',
      artistSpotifyId: artist.spotifyId || '',
    } : a));
    setOpenDropdown(-1);
    setSpotifyStatus(s => ({ ...s, [index]: artist.spotifyId ? 'found' : undefined }));
  };

  const handleTimeChange = (name, hhmm) => setField(name, hhmm ? `${hhmm}:00` : '');

  const toNum = (v) => (v === '' || v == null ? null : Number(v));

  const isValidInstagramUrl = (url) => {
    if (!url) return true;
    try {
      const u = new URL(url);
      return u.hostname === 'www.instagram.com' || u.hostname === 'instagram.com';
    } catch { return false; }
  };

  const buildPayload = () => ({
    title: form.title,
    descriptionGerman: form.descriptionGerman,
    descriptionEnglish: form.descriptionEnglish,
    musicGenre: form.selectedGenres,
    image: form.instagramImageUrl || '',
    instagramName: form.instagramName || '',
    address: form.address || '',
    entryPrice: toNum(form.entryPrice),
    minAge: toNum(form.minAge),
    dressCode: form.dressCode || '',
    drinkPriceIndicator: toNum(form.drinkPriceIndicator),
    latitude: toNum(form.latitude),
    longitude: toNum(form.longitude),
    markerType: 'Event',
    specialType: form.specialType || null,
    artists: artists.filter(a => a.name.trim()).map(a => ({
      name: a.name.trim(),
      instagram: a.instagram.trim() || null,
      artistSpotifyId: a.artistSpotifyId.trim() || null,
    })),
    ticketUrl: form.ticketUrl || '',
    eventDate: form.eventDate || '',
    startTime: form.startTime || '',
    endTime: form.endTime || '',
    hostCode: form.hostCode || '',
    boxOfficePrice: toNum(form.boxOfficePrice),
    boxOfficeAvailability: form.boxOfficeAvailability || null,
    hasAwarenessTeam: form.hasAwarenessTeam,
  });

  const validate = () => {
    if (!form.title.trim()) return 'Titel ist erforderlich.';
    if (!form.eventDate) return 'Event-Datum ist erforderlich.';
    if (!isValidInstagramUrl(form.instagramImageUrl)) return 'Bitte eine gültige Instagram-URL eintragen.';
    if (form.latitude !== '' && Number.isNaN(toNum(form.latitude))) return 'Latitude muss Zahl sein.';
    if (form.longitude !== '' && Number.isNaN(toNum(form.longitude))) return 'Longitude muss Zahl sein.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null);
    const v = validate();
    if (v) { setErr(v); return; }
    try {
      setLoading(true);
      await MarkersAPI.createEvent(buildPayload());
      setMsg('Event erfolgreich erstellt!');
      setTimeout(() => navigate('/events'), 1200);
    } catch (ex) {
      console.error(ex);
      setErr('Fehler beim Erstellen des Events.');
    } finally {
      setLoading(false);
    }
  };

  const toggleChip = (field, value) =>
    setField(field, form[field] === value ? '' : value);

  const toggleGenre = (g) => setField('selectedGenres',
    form.selectedGenres.includes(g)
      ? form.selectedGenres.filter(x => x !== g)
      : [...form.selectedGenres, g]
  );

  return (
    <div className="ec-page">
      <div className="page-header">
        <PageTitle title="Event anlegen" description="Ein Event von Hand eintragen. Für viele Events auf einmal nimm den CSV-Import unter „Events & Clubs“." />
      </div>

      <form className="ec-form-single" onSubmit={handleSubmit}>

        {/* ── Allgemein ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Allgemein
          </div>
          <div className="ec-field">
            <label className="ec-label">Titel *</label>
            <input className="ec-input" value={form.title}
              onChange={e => setField('title', e.target.value)} placeholder="z. B. Opening Night" required />
          </div>
          <div className="ec-row">
            <div className="ec-field">
              <label className="ec-label">Instagram Handle</label>
              <input className="ec-input" value={form.instagramName}
                onChange={e => setField('instagramName', e.target.value)} placeholder="event_handle" />
            </div>
            <div className="ec-field">
              <label className="ec-label">Ticket-URL</label>
              <input className="ec-input" type="url" value={form.ticketUrl}
                onChange={e => setField('ticketUrl', e.target.value)} placeholder="https://…" />
            </div>
            <div className="ec-field">
              <label className="ec-label">Host-Code</label>
              <input className="ec-input" value={form.hostCode}
                onChange={e => setField('hostCode', e.target.value)} placeholder="optional"
                autoComplete="off" />
            </div>
          </div>
        </div>

        {/* ── Beschreibung ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Beschreibung
          </div>
          <div className="ec-row">
            <div className="ec-field">
              <label className="ec-label">Deutsch</label>
              <textarea className="ec-textarea" value={form.descriptionGerman}
                onChange={e => setField('descriptionGerman', e.target.value)} placeholder="Kurze Beschreibung auf Deutsch" />
            </div>
            <div className="ec-field">
              <label className="ec-label">English</label>
              <textarea className="ec-textarea" value={form.descriptionEnglish}
                onChange={e => setField('descriptionEnglish', e.target.value)} placeholder="Short description in English" />
            </div>
          </div>
        </div>

        {/* ── Genres ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
            Genres
          </div>
          <div className="mk-chips">
            {ALL_GENRES.map(g => (
              <button key={g} type="button"
                className={`mk-chip${form.selectedGenres.includes(g) ? ' mk-chip--active-orange' : ''}`}
                onClick={() => toggleGenre(g)}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* ── Artists ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Artists
          </div>
          <div className="ec-artist-list">
            {artists.map((artist, i) => {
              const suggestions = getSuggestions(artist.name);
              const status = spotifyStatus[i];
              return (
                <div key={i} className="ec-artist-row">
                  <div className="ec-field">
                    <label className="ec-label">Name</label>
                    <div className="ec-artist-name-wrap">
                      <input
                        className="ec-input"
                        value={artist.name}
                        onChange={e => handleArtistNameChange(i, e.target.value)}
                        onFocus={() => artist.name.length >= 2 && setOpenDropdown(i)}
                        onBlur={() => setTimeout(() => setOpenDropdown(-1), 150)}
                        placeholder="DJ Alpha"
                        autoComplete="off"
                      />
                      {openDropdown === i && suggestions.length > 0 && (
                        <div className="ec-artist-suggestions">
                          {suggestions.map(a => (
                            <button key={a.id} type="button" className="ec-artist-suggestion"
                              onMouseDown={() => selectSuggestion(i, a)}>
                              <span className="ec-sug-name">{a.name}</span>
                              <span className="ec-sug-meta">
                                {a.instagram && <span className="ec-sug-ig">@{a.instagram}</span>}
                                {a.spotifyId && (
                                  <span className="ec-sug-spotify">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11">
                                      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.622.622 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 0 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.13-9.965-1.166a.78.78 0 0 1-.973-.519.781.781 0 0 1 .519-.972c3.632-1.102 8.147-.568 11.234 1.328a.78.78 0 0 1 .257 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.793c3.532-1.072 9.404-.865 13.115 1.338a.937.937 0 0 1-.955 1.612z"/>
                                    </svg>
                                  </span>
                                )}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {status === 'loading' && <span className="ec-spotify-status ec-spotify-status--loading">Suche Spotify…</span>}
                    {status === 'found'   && <span className="ec-spotify-status ec-spotify-status--found">✓ Spotify ID gefunden</span>}
                    {status === 'none'    && <span className="ec-spotify-status ec-spotify-status--none">Kein Spotify-Treffer</span>}
                  </div>
                  <div className="ec-field">
                    <label className="ec-label">Instagram</label>
                    <input className="ec-input" value={artist.instagram}
                      onChange={e => setArtistField(i, 'instagram', e.target.value)} placeholder="artist_handle" />
                  </div>
                  <div className="ec-field">
                    <label className="ec-label">Spotify ID</label>
                    <input className="ec-input" value={artist.artistSpotifyId}
                      onChange={e => setArtistField(i, 'artistSpotifyId', e.target.value)} placeholder="1WNoKxsp715jez1Td4vthc" />
                  </div>
                  <button type="button" className="ec-artist-remove" onClick={() => removeArtist(i)}
                    disabled={artists.length === 1} aria-label="Artist entfernen">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            <button type="button" className="ec-add-artist" onClick={addArtist}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Artist hinzufügen
            </button>
          </div>
        </div>

        {/* ── Titelbild ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            Titelbild
          </div>
          <div className="ec-field">
            <label className="ec-label">Instagram URL</label>
            <input className="ec-input" type="url" value={form.instagramImageUrl}
              onChange={e => setField('instagramImageUrl', e.target.value)}
              placeholder="https://www.instagram.com/p/…" />
          </div>
          {form.instagramImageUrl && (
            <a className="ec-ig-link" href={form.instagramImageUrl} target="_blank" rel="noreferrer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Vorschau öffnen
            </a>
          )}
        </div>

        {/* ── Termin ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Termin
          </div>
          <div className="ec-row-3">
            <div className="ec-field">
              <label className="ec-label">Datum *</label>
              <input className="ec-input" type="date" value={form.eventDate}
                onChange={e => setField('eventDate', e.target.value)} required />
            </div>
            <div className="ec-field">
              <label className="ec-label">Beginn</label>
              <input className="ec-input" type="time"
                value={form.startTime ? form.startTime.slice(0, 5) : ''}
                onChange={e => handleTimeChange('startTime', e.target.value)} />
            </div>
            <div className="ec-field">
              <label className="ec-label">Ende</label>
              <input className="ec-input" type="time"
                value={form.endTime ? form.endTime.slice(0, 5) : ''}
                onChange={e => handleTimeChange('endTime', e.target.value)} />
            </div>
          </div>
        </div>

        {/* ── Ort ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Veranstaltungsort
          </div>
          <div className="ec-field">
            <label className="ec-label">Adresse</label>
            <input className="ec-input" value={form.address}
              onChange={e => setField('address', e.target.value)} placeholder="Straße 1, 1010 Wien" />
          </div>
          <div className="ec-row">
            <div className="ec-field">
              <label className="ec-label">Latitude</label>
              <input className="ec-input" inputMode="decimal" value={form.latitude}
                onChange={e => setField('latitude', e.target.value)} placeholder="48.2082" />
            </div>
            <div className="ec-field">
              <label className="ec-label">Longitude</label>
              <input className="ec-input" inputMode="decimal" value={form.longitude}
                onChange={e => setField('longitude', e.target.value)} placeholder="16.3738" />
            </div>
          </div>
        </div>

        {/* ── Preise & Einlass ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            Preise &amp; Einlass
          </div>
          <div className="ec-row">
            <div className="ec-field">
              <label className="ec-label">Eintritt (€)</label>
              <input className="ec-input" inputMode="decimal" value={form.entryPrice}
                onChange={e => setField('entryPrice', e.target.value)} placeholder="z. B. 15" />
            </div>
            <div className="ec-field">
              <label className="ec-label">Mindestalter</label>
              <input className="ec-input" inputMode="numeric" value={form.minAge}
                onChange={e => setField('minAge', e.target.value)} placeholder="z. B. 18" />
            </div>
          </div>
          <div className="ec-field">
            <label className="ec-label">Drink-Preis-Index</label>
            <div className="mk-price-group">
              {ALL_PRICES.map(p => (
                <button key={p} type="button"
                  className={`mk-price-btn${form.drinkPriceIndicator === p ? ' mk-price-btn--active-orange' : ''}`}
                  onClick={() => setField('drinkPriceIndicator', form.drinkPriceIndicator === p ? '' : p)}>
                  {'€'.repeat(p)}
                </button>
              ))}
            </div>
          </div>
          <div className="ec-field">
            <label className="ec-label">Dresscode</label>
            <div className="mk-chips">
              {ALL_DRESSCODES.map(d => (
                <button key={d.value} type="button"
                  className={`mk-chip${form.dressCode === d.value ? ' mk-chip--active-orange' : ''}`}
                  onClick={() => toggleChip('dressCode', d.value)}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Special Type ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Special Type
          </div>
          <div className="mk-chips">
            {ALL_SPECIAL_TYPES.map(t => (
              <button key={t.value} type="button"
                className={`mk-chip${form.specialType === t.value ? ' mk-chip--active-orange' : ''}`}
                onClick={() => toggleChip('specialType', t.value)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Box Office ── */}
        <div className="ec-card">
          <div className="ec-section ec-section--first">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M22 10H2"/></svg>
            Box Office
          </div>
          <div className="ec-row">
            <div className="ec-field">
              <label className="ec-label">Box-Office Preis (€)</label>
              <input className="ec-input" inputMode="decimal" value={form.boxOfficePrice}
                onChange={e => setField('boxOfficePrice', e.target.value)} placeholder="z. B. 20" />
            </div>
          </div>
          <div className="ec-field">
            <label className="ec-label">Verfügbarkeit</label>
            <div className="mk-chips">
              {BOX_OFFICE_AVAILABILITY.map(a => (
                <button key={a.value} type="button"
                  className={`mk-chip${form.boxOfficeAvailability === a.value ? ' mk-chip--active-orange' : ''}`}
                  onClick={() => toggleChip('boxOfficeAvailability', a.value)}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ec-toggle-row">
            <div>
              <div className="ec-toggle-label">Awareness Team</div>
              <div className="ec-toggle-sub">Ist ein Awareness-Team vor Ort?</div>
            </div>
            <input type="checkbox" className="ec-checkbox" checked={form.hasAwarenessTeam}
              onChange={e => setField('hasAwarenessTeam', e.target.checked)} />
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="ec-actions">
          <button className="ec-btn ec-btn-primary" type="submit" disabled={loading}>
            <svg className="ec-icon-plus" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {loading ? 'Speichern…' : 'Event anlegen'}
          </button>
        </div>

        {err && <p className="ec-error">{err}</p>}
        {msg && <p className="ec-success">{msg}</p>}

      </form>
    </div>
  );
}
